'use client';

import { useEffect, useState } from 'react';
import { useStore as useRfStore } from '@xyflow/react';
import type { GuideLine } from '@/lib/workspace/snap';

/**
 * SVG overlay that paints the currently-active alignment guides.
 *
 * Coordinate system: SVG `viewport` (CSS pixels on the canvas wrapper).
 * Each guide is given in flow coords; we convert through the live RF
 * transform so the lines stay glued to the candidate edges even as
 * the user pans / zooms during the drag.
 *
 * Subscribes to a global event (`workspace:alignment-guides`) so
 * workspace-canvas can publish guide line segments while a drag is in
 * flight without owning the SVG itself.
 *
 * Visual tuning (Figjam / Confluence-style):
 *   - 1.5px stroke in primary blue so the line clearly reads as
 *     "snap target", not as canvas grid texture.
 *   - Tight `4 4` dash so the line still looks dotted at zoom levels
 *     near 1.0 but stays visually continuous (not Morse code) when
 *     the user zooms out.
 *   - Endpoint dots at each line's start / end so the guide visually
 *     "terminates" at the involved nodes — a flourish that matches
 *     Figjam's smart-guide style and helps the user spot exactly
 *     which two nodes are aligning.
 */
const GUIDE_COLOR = '#FF4D8F';

export function AlignmentGuides() {
  const [guides, setGuides] = useState<GuideLine[]>([]);
  // RF stores its viewport in the internal store; tying to it here
  // is the cheapest way to get re-renders on every pan/zoom frame so
  // the guides keep up with the camera while drawn.
  const tx = useRfStore((s) => s.transform[0]);
  const ty = useRfStore((s) => s.transform[1]);
  const tz = useRfStore((s) => s.transform[2]);

  useEffect(() => {
    const onGuides = (e: Event) => {
      const ev = e as CustomEvent<{ guides: GuideLine[] }>;
      setGuides(ev.detail?.guides ?? []);
    };
    window.addEventListener('workspace:alignment-guides', onGuides);
    return () =>
      window.removeEventListener('workspace:alignment-guides', onGuides);
  }, []);

  if (guides.length === 0) return null;

  // Convert a flow coordinate to a screen-space coordinate using the
  // current viewport. RF's `flowToScreenPosition` would work too but
  // we already have tx/ty/tz from the store subscription so the math
  // is one multiply away.
  const flowToScreen = (axis: 'x' | 'y', value: number) => {
    if (axis === 'x') return value * tz + tx;
    return value * tz + ty;
  };

  return (
    <svg
      aria-hidden
      // z-[25] puts the guides above React Flow's edge SVG (z-auto)
      // and the node layer (which RF sets to z 1000 on the dragged
      // node — see `react-flow__node.draggable.dragging` in xyflow's
      // base CSS). Without bumping above 1000 the dragged node would
      // paint OVER the guide line, hiding it.
      className="pointer-events-none absolute inset-0"
      style={{ width: '100%', height: '100%', zIndex: 1001 }}
    >
      {guides.map((g, i) => {
        if (g.axis === 'x') {
          const x = flowToScreen('x', g.pos);
          const y1 = flowToScreen('y', g.start);
          const y2 = flowToScreen('y', g.end);
          return (
            <g key={`v-${i}`}>
              <line
                x1={x}
                y1={y1}
                x2={x}
                y2={y2}
                stroke={GUIDE_COLOR}
                strokeWidth={1.5}
                strokeDasharray="4 4"
                opacity={0.9}
              />
              {/* Endpoint dots — small filled circles so the guide
                  visually anchors to the aligned nodes. */}
              <circle cx={x} cy={y1} r={2} fill={GUIDE_COLOR} />
              <circle cx={x} cy={y2} r={2} fill={GUIDE_COLOR} />
            </g>
          );
        }
        const y = flowToScreen('y', g.pos);
        const x1 = flowToScreen('x', g.start);
        const x2 = flowToScreen('x', g.end);
        return (
          <g key={`h-${i}`}>
            <line
              x1={x1}
              y1={y}
              x2={x2}
              y2={y}
              stroke={GUIDE_COLOR}
              strokeWidth={1.5}
              strokeDasharray="4 4"
              opacity={0.9}
            />
            <circle cx={x1} cy={y} r={2} fill={GUIDE_COLOR} />
            <circle cx={x2} cy={y} r={2} fill={GUIDE_COLOR} />
          </g>
        );
      })}
    </svg>
  );
}
